import React, { useState, useEffect, useRef } from "react";
import TextFieldGroup from "../../commons/TextFieldGroup";
import Searchbar from "../../commons/Searchbar";

import {
  Layout,
  Breadcrumb,
  Form,
  Table,
  Row,
  Col,
  Button,
  Divider,
  message,
  Space,
  List,
} from "antd";

import {
  formItemLayout,
  tailFormItemLayout,
  smallFormItemLayout,
  smallTailFormItemLayout,
} from "./../../utils/Layouts";
import {
  EditOutlined,
  CloseOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import isEmpty from "../../validation/is-empty";
import { useSelector } from "react-redux";
import {
  edit,
  onDelete,
  onSubmit,
  onSearch,
  onChange,
} from "../../utils/form_utilities";
import ReportHeading from "../../utils/ReportHeading";
import moment from "moment";
import RangeDatePickerFieldGroup from "../../commons/RangeDatePickerFieldGroup";
import axios from "axios";
import numberFormat from "../../utils/numberFormat";
import {
  addKeysToArray,
  onBranchSearch,
  onCategorySearch,
  onCustomerSearch,
  onStockSearch,
} from "../../utils/utilities";
import round from "../../utils/round";
import { sumBy } from "lodash";
import ReactToPrint from "react-to-print";
import SelectFieldGroup from "../../commons/SelectFieldGroup";
import DatePickerFieldGroup from "../../commons/DatePickerFieldGroup";
import numberFormatInt from "../../utils/numberFormatInt";
import { useCallback } from "react";
import SimpleSelectFieldGroup from "../../commons/SimpleSelectFieldGroup";

const { Content } = Layout;

const url = "/api/sales/";
const title = "Category Sales Report";

const initialValues = {
  period_covered: [moment(), moment()],
};
export default function CategorySalesReport() {
  const [errors, setErrors] = useState({});
  const [records, setRecords] = useState([]);
  const auth = useSelector((state) => state.auth);
  const [state, setState] = useState(initialValues);
  const [options, setOptions] = useState({
    customers: [],
    branches: [],
  });

  const report = useRef(null);

  const records_column = [
    {
      title: "Item",
      dataIndex: ["product"],
    },
    {
      title: "Quantity",
      dataIndex: ["quantity"],
      width: 100,
      align: "right",
      render: (value, record) => (record.footer !== 1 ? value : "Sub Total: "),
    },
    {
      title: "Amount",
      dataIndex: ["amount"],
      width: 100,
      align: "right",
      render: (value) => <span>{numberFormat(value)}</span>,
    },
  ];

  const getReport = useCallback(
    ({ period_covered, branch, account, category, ...rest }) => {
      if (period_covered) {
        const form_data = {
          period_covered,
          branch,
          account,
          category,
          ...rest,
        };

        const loading = message.loading("Loading...");
        axios
          .post(`${url}category-sales-report`, form_data)
          .then((response) => {
            loading();
            if (response.data) {
              if (!form_data.is_excel) {
                return setRecords(response.data.records);
              }
              window.open(`/public/reports/${response.data.filename}`);
            }
          })
          .catch((err) => {
            loading();
            message.error("There was an error processing your request");
          });
      }
    },
    []
  );

  return (
    <Content className="content-padding">
      <div className="columns is-marginless">
        <div className="column">
          <Breadcrumb style={{ margin: "16px 0" }}>
            <Breadcrumb.Item>Reports</Breadcrumb.Item>
            <Breadcrumb.Item>{title}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>

      <div style={{ background: "#fff", padding: 24, minHeight: 280 }}>
        <span className="module-title">{title}</span>
        <Divider />
        <Row>
          <Col span={12}>
            <RangeDatePickerFieldGroup
              label="Period Covered"
              name="period_covered"
              value={state.period_covered}
              onChange={(dates) =>
                setState((prevState) => ({
                  ...prevState,
                  period_covered: dates,
                }))
              }
              error={errors.period_covered}
              formItemLayout={formItemLayout}
            />
          </Col>
          <Col span={12}>
            <SelectFieldGroup
              label="Category"
              value={state.category?.name}
              onSearch={(value) =>
                onCategorySearch({ value, options, setOptions })
              }
              onChange={(index) => {
                const category = options.categories?.[index] || null;
                setState((prevState) => ({
                  ...prevState,
                  category,
                }));
              }}
              error={errors?.category}
              formItemLayout={formItemLayout}
              data={options.categories}
              column="name"
            />
          </Col>
        </Row>
        <Row>
          <Col offset={2} span={12}>
            <Form.Item className="field is-grouped">
              <Space>
                <Button
                  onClick={() => {
                    getReport({
                      ...state,
                    });
                  }}
                >
                  Search
                </Button>
                <Button
                  onClick={() => {
                    getReport({
                      ...state,
                      is_excel: true,
                    });
                  }}
                >
                  Download to Excel
                </Button>
                <ReactToPrint
                  trigger={() => (
                    <Button type="primary" icon={<PrinterOutlined />}>
                      Print
                    </Button>
                  )}
                  bodyClass="print"
                  content={() => report.current}
                />
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <div ref={report}>
          <div>
            <div className="has-text-weight-bold">
              Category Sales Invoices Report
            </div>
            <div>
              Period Covered: {state.period_covered?.[0]?.format("MM/DD/YYYY")}{" "}
              to {state.period_covered?.[1]?.format("MM/DD/YYYY")}
            </div>
            <div>
              {state.branch?.company?.name || ""} - {state.branch?.name || ""}
            </div>
          </div>
          <List
            dataSource={records}
            renderItem={(record) => (
              <div className="m-t-1">
                <div>{record.category}</div>
                <Table
                  size="small"
                  dataSource={addKeysToArray([
                    ...(record.items || []),
                    {
                      footer: 1,
                      amount: sumBy(record.items || [], (o) => o.amount),
                    },
                  ])}
                  columns={records_column}
                  pagination={false}
                  rowClassName={(record, index) => {
                    if (record.footer === 1) {
                      return "footer-summary has-text-weight-bold";
                    }
                  }}
                />
              </div>
            )}
          />
          <div className="has-text-right has-text-weight-bold">
            Grand Total :{" "}
            {numberFormat(sumBy(records, (o) => o.category_total))}
          </div>
        </div>
      </div>
    </Content>
  );
}
